/* eslint-disable camelcase */
import { capitalize, get, isObject } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, Fragment, useMemo } from 'react';
import { ButtonGroup, Col, Container, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import * as Yup from 'yup';
import formatDate from '../../../../frontend/components/components/utils/formatDate';
import i18n from '../../../../javascript/utils/i18n';
import Checkbox from '../Checkbox/Checkbox';
import Checkboxes from '../Checkbox/Checkboxes';
import Dropzone, { DROPZONE_STATE } from '../Dropzone/Dropzone';
import FormControl from '../Form/FormControl';
import Select from '../Select';

import { useSelector } from 'react-redux';
import './ObjectAttribute.scss';
import Button from '../Button';
import { isEmpty } from 'lodash-es';

const urlRegex = /^(((https?):)?\/\/)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;

/**
 * @param {ObjectAttributeProps} props
 * @returns
 */
const ObjectAttribute = ({
  fieldData,
  onChange,
  onBlur,
  value,
  disabled,
  error,
  id,
  name,
  showColumnLabel = false,
  columnLabelFirst = false,
  editable,
  ...props
}) => {
  const fieldName = name || `${fieldData.object_type_form_string}[${fieldData.code}]`;
  const displayType = fieldData.display_type;
  const url = (
    value &&
    (fieldData.ui_element == 'file_upload' || fieldData.ui_element == 'pdf_upload') &&
    value.startsWith('//')) ? `https:${value}` : value;
  const current_admin_roles = useSelector(store => get(store, 'currentCampaign.campaign.current_admin_roles'));
  const agent = useSelector(store => get(store, 'auth.agent'));
  const user = useSelector(store => get(store, 'auth.user'));

  const handleChange = useCallback((event, meta) => {
    switch (fieldData.ui_element) {
      case UIElementType.TEXT:
      case UIElementType.TEXT_AREA:
      case UIElementType.DATE:
      case UIElementType.DROPDOWN:
      case UIElementType.RADIO:
      case UIElementType.LINK:
      case UIElementType.NUMBER:
        if (onChange) onChange(event);
        break;
      case UIElementType.CHECKBOX:
        if (onBlur) onBlur(event);
        if (onChange) onChange(event);
        break;
      case UIElementType.CHECKBOXES:
        if (onBlur) onBlur(event);
        if (onChange) {
          onChange({
            target: {
              value: meta.values,
              name: fieldName
            }
          });
        }
        break;
      case UIElementType.PDF_UPLOAD:
        if (onChange) {
          if (event === DROPZONE_STATE.SUCCESS && meta.success) {
            onChange({
              target: {
                value: meta.fileUrl,
                name: fieldName || id
              }
            });
          }
        }
        break;
      case UIElementType.FILE_UPLOAD:
        if (onChange) {
          if (event === DROPZONE_STATE.SUCCESS && meta.success) {
            onChange({
              target: {
                value: meta.fileUrl,
                name: fieldName || id
              }
            });
          }
        }
        break;
    }
  }, [fieldData]);

  const handleBlur = useCallback((event, meta) => {
    switch (fieldData.ui_element) {
      case UIElementType.TEXT:
      case UIElementType.TEXT_AREA:
      case UIElementType.DATE:
      case UIElementType.LINK:
      case UIElementType.NUMBER:
        if (onBlur) onBlur(event);
        break;
    }
  }, [fieldData]);

  const isValidNumberInput = (keyPressed, target) => {
    const currentValue = target.value;

    return (
      /^\d$/.test(keyPressed) ||
      (["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(keyPressed)) ||
      (keyPressed === "-" && !currentValue.includes("-") && currentValue.length === 0) ||
      (keyPressed === "." && !currentValue.includes("."))
    )
  };

  const handleKeyDown = useCallback((event) => {
    if (fieldData.ui_element == UIElementType.NUMBER && !isValidNumberInput(event.key, event.currentTarget))
      event.preventDefault();
  }, [fieldData]);

  const formLabel = !showColumnLabel ?
    <div dangerouslySetInnerHTML={{ __html: fieldData.label }} /> :
    <div>
      {columnLabelFirst && <div className='font-weight-bold'>{fieldData.column_label}</div>}
      {fieldData.label && <Fragment>
        <div dangerouslySetInnerHTML={{ __html: fieldData.label }} />
      </Fragment>
      }
      {!columnLabelFirst && <div className='font-weight-bold'>{fieldData.column_label}</div>}
    </div>;

  const isAnEditableField = useMemo(() => (
    (user && fieldData.is_editable_by_influencer) ||
    (agent && fieldData.is_editable_by_client) ||
    (user && user.admin) ||
    (agent && agent.admin) ||
    editable ||
    !isEmpty(current_admin_roles)
  ), [user, agent, fieldData, editable, current_admin_roles]);

  const disabledField = disabled || !isAnEditableField;
  const requiredField = !fieldData.optional && !disabledField;

  switch (fieldData.ui_element) {
    case UIElementType.TEXT:
      return (
        <Form.Group>
          <Form.Label required={requiredField}>{formLabel}</Form.Label>
          <FormControl
            name={fieldName}
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            disabled={disabledField}
            error={!!error}
            maxLength={499}
            {...props}
          />
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.TEXT_AREA:
      return (
        <Form.Group>
          <Form.Label required={requiredField}>{formLabel}</Form.Label>
          <FormControl
            name={fieldName}
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            disabled={disabledField}
            error={!!error}
            textarea
            {...props}
          />
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.CHECKBOX:
      return (
        <Form.Group>
          <Checkbox
            name={fieldName}
            onChange={handleChange}
            value={!!value}
            disabled={disabledField}
            label={formLabel}
            required={requiredField}
          />
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.CHECKBOXES:
      return (
        <Checkboxes
          fieldData={fieldData}
          name={fieldName}
          onChange={handleChange}
          value={(value && value.toString().split(",")) || []}
          disabled={disabledField}
          formLabel={formLabel}
          required={requiredField}
          error={error}
        />
      );
    case UIElementType.DATE:
      return (
        <Form.Group>
          <Form.Label required={requiredField}>{formLabel}</Form.Label>
          <FormControl
            name={fieldName}
            type="date"
            onChange={handleChange}
            onBlur={handleBlur}
            value={formatDate(value, { format: 'YYYY-MM-DD' })}
            disabled={disabledField}
            error={!!error}
            {...props}
          />
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.FILE_UPLOAD:
      return (
        <Form.Group>
          {url && /\.(mp4)$/.exec(url) &&
            <video controls className='w-100 object-attribute-media' key={url}>
              <source src={url} />
            </video>}
          {url && /\.(bmp|jpeg|jpg|png|gif)$/.exec(url) &&
            <img src={url} key={url} className='object-attribute-media' alt="" />}
          {url && !/\.(bmp|jpeg|jpg|png|gif)$/.exec(url) &&
            <div>{url}</div>
          }
          <Form.Label required={requiredField}>{formLabel}</Form.Label>
          {isAnEditableField && (
            <Dropzone
              name={fieldName}
              selectFiles
              onChange={handleChange}
            />
          )}
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.PDF_UPLOAD:
      return (
        <Form.Group>
          {url && /\.(pdf)$/.exec(url) && (
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          )}
          <Form.Label required={requiredField}>{formLabel}</Form.Label>
          {isAnEditableField && (
            <Dropzone
              name={fieldName}
              selectFiles
              onChange={handleChange}
            />
          )}
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.RADIO:
      return <Form.Group>
        <Form.Label required={requiredField}>{formLabel}</Form.Label>
        {fieldData.options.map((option) =>
          <InputGroup key={option.id} className='object-attribute radio-group z-index-1' >
            <input
              type="radio"
              id={option.id}
              name={fieldName}
              value={option.id}
              onChange={handleChange}
              checked={option.id == value}
              disabled={disabledField}
              {...props} />
            <Form.Label htmlFor={option.id}>
              {option.label}
            </Form.Label>
          </InputGroup>)
        }
        {error && <Form.Text className="text-muted danger-text">
          {error}
        </Form.Text>}
      </Form.Group>;
    case UIElementType.DROPDOWN:
      return <Form.Group>
        <Form.Label required={requiredField}>{formLabel}</Form.Label>
        <Select
          name={fieldName}
          value={value}
          onChange={handleChange}
          error={!!error}
          disabled={disabledField}
          isMulti={fieldData.is_multiple}
          options={fieldData.options.map(option => ({
            label: option.label,
            value: option.id,
          }))}
          {...props} />
        {error && <Form.Text className="text-muted danger-text">
          {error}
        </Form.Text>}
      </Form.Group>;
    case UIElementType.LINK:
      return (
        <Form.Group>
          <Form.Label required={requiredField}>{formLabel}</Form.Label>
          <FormControl
            name={fieldName}
            type="text"
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            disabled={disabledField}
            error={!!error}
            maxLength={499}
            {...props}
          />
          {error && <Form.Text className="text-muted danger-text">
            {error}
          </Form.Text>}
        </Form.Group>
      );
    case UIElementType.NUMBER:
      return (
        <Form.Group>
          <Form.Label required={!fieldData.optional}>{formLabel}</Form.Label>
          <FormControl
            name={fieldName}
            type="number"
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            disabled={disabled}
            error={!!error}
            maxLength={499}
            inputMode="numeric"
            pattern="\d*"
            onKeyDown={handleKeyDown}
            {...props}
          />
          {
            error &&
            <Form.Text className="text-muted danger-text">
              {error}
            </Form.Text>
          }
        </Form.Group>
      );
  }
};

ObjectAttribute.propTypes = {
  fieldData: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
    PropTypes.array
  ]),
  disabled: PropTypes.bool,
  error: PropTypes.any,
  id: PropTypes.string,
  name: PropTypes.string,
  showColumnLabel: PropTypes.bool
};

export default ObjectAttribute;

/**
 * @typedef ObjectAttributeShape
 * @property {number} id
 * @property {string} label
 * @property {string} code
 * @property {boolean} [is_multiple=false]
 * @property {boolean} [is_editable_by_client=false]
 * @property {boolean} [is_editable_by_influencer=false]
 * @property {boolean} [has_options=false]
 * @property {boolean} [optional]
 * @property {('string'|'boolean')} data_type
 * @property {string} object_type_form_string
 * @property {string} [validation_expr]
 * @property {('text'|'text_area'|'date'|'checkbox'|'file_upload'|'radio'|'dropdown')} ui_element
 * @property {[{
 *  id: number,
 *  label: string
 * }]} [options]
 *
 * @typedef ObjectAttributeCustom
 * @property {ObjectAttributeShape} fieldData
 * @property {()=> void} onChange
 * @property {()=> void} onBlur
 * @property {any} value
 * @property {boolean} [disabled=false]
 * @property {boolean} [showColumnLabel=false]
 *
 * @typedef {import('react').InputHTMLAttributes & ObjectAttributeCustom } ObjectAttributeProps
 */

const MEDIA_STYLES = {
  width: '100%',
  maxHeight: 300,
  objectFit: 'contain'
};


/**
 * @readonly
 * @enum {string}
 */
export const UIElementType = {
  TEXT: 'text',
  TEXT_AREA: 'text_area',
  DATE: 'date',
  CHECKBOX: 'checkbox',
  CHECKBOXES: 'checkboxes',
  FILE_UPLOAD: 'file_upload',
  RADIO: 'radio',
  DROPDOWN: 'dropdown',
  LINK: 'link',
  PDF_UPLOAD: 'pdf_upload',
  NUMBER: 'number'
};

export const displayObjAttrValues = (displayType, values) => {
  if (!values.length) return '';

  switch (displayType) {
    case 'video':
      return <video controls style={MEDIA_STYLES}>
        <source src={isObject(values[0]) ? get(values[0], 'value') : values[0]} />
      </video>;
    case 'image':
      return <img src={isObject(values[0]) ? get(values[0], 'value') : values[0]} style={MEDIA_STYLES} />;
    case 'date':
      return formatDate(isObject(values[0]) ? get(values[0], 'value') : values[0]);
    case 'link':
    case 'pdf':
      return <a className="text-3 text-truncate d-inline-block w-100"
        href={isObject(values[0]) ? get(values[0], 'value') : values[0]} target='_blank' rel="noreferrer" >{isObject(values[0]) ? get(values[0], 'value') : values[0]}</a>;
    default:
      return (
        <div className="text-3">
          {values.map((item, i) => <div key={i}> {`${isObject(item) ? get(item, 'value') : item}`} </div>)}
        </div>
      );
  }
};

export const validateObjAttrValuesInput = (
  datatype = 'string',
  isMultiple = false,
  ui_element = '',
  optional = false) =>
  (v = false) => {
    if (optional) return;

    if (isMultiple) {
      if (Array.isArray(v)) {
        return Yup.array().min(1).required().validate(v.filter(item => item !== ''))
          .then(() => null)
          .catch(() => i18n.t("common.forms.is_required"));
      }
      return Yup.string().required().validate(v)
        .then(() => null)
        .catch(() => i18n.t("common.forms.is_required"));

    } else if (datatype == 'string') {
      if (ui_element == 'link') {
        return Yup.string().matches(urlRegex).validate(v)
          .then(() => null)
          .catch(() => i18n.t('influencers.common.forms.is_not_valid_url'));
      }
      return Yup.string().required().validate(v)
        .then(() => null)
        .catch(() => i18n.t("common.forms.is_required"));

    } else if (datatype == 'int') {
      return Yup.number().required().validate(v)
        .then(() => null)
        .catch(() => i18n.t("common.forms.is_required"));
    } else if (datatype == 'boolean') {
      return Yup.boolean().oneOf([true]).validate(v)
        .then(() => null)
        .catch(() => i18n.t("common.forms.is_required"));
    } else if (datatype == 'datetime') {
      return Yup.date().required().validate(v)
        .then(() => null)
        .catch(() => i18n.t("common.forms.is_required"));
    }
  };

export const editObjectAttrValueModal = ({ show, objAttr, formik, onCancel, onSave, onReset, editable, adminView }) => {
  if (!objAttr) return;

  const code = objAttr.code;
  const isTouched = formik.touched && formik.touched[code];
  const hasError = formik.errors && formik.errors[code];

  return (
    <Modal
      show={show}
      onHide={() => onCancel()}
      centered
      size="sm"
      className=''>
      <Modal.Body className="text-1">
        <div className="h6 mb-4 mt-3">
          {i18n.t('common.editing')}&nbsp;
          <span className='font-weight-bold'>{capitalize(objAttr.column_label)}</span>
        </div>
        <Container className='p-0 mw-100'>
          <Row className="no-gutters mb-2">
            <Col xs={12}>
              <ObjectAttribute
                fieldData={objAttr}
                name={code}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values[code]}
                error={isTouched && hasError}
                showColumnLabel
                editable={editable}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <ButtonGroup>
              { onReset &&
                <Button onClick={onReset} >
                  {i18n.t('common.reset')}
                </Button>
              }
              <Button onClick={() => onCancel()}>
                {i18n.t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={!adminView && hasError}
                onClick={() => onSave(objAttr)}
              >
                {i18n.t('common.save')}
              </Button>
            </ButtonGroup>
          </div>
        </Container>
      </Modal.Body>
    </Modal >
  );
};

export const generateOASchema = (oasMetadata, t) => {
  const editableAttrsSchema = {};
  console.log('oasMetadata', oasMetadata);

  oasMetadata.forEach((attr) => {
    if (!attr.optional && attr.is_editable_by_influencer) {
      if ([UIElementType.CHECKBOXES].includes(attr.ui_element)) {
        editableAttrsSchema[attr.code] = Yup.array().min(1).required();
      } else {
        editableAttrsSchema[attr.code] = Yup.mixed().required(t('common.forms.is_required'));
      }
    }
  });
  return editableAttrsSchema;
};
